import startCase from 'lodash/startCase';
import { useTracking } from 'react-tracking';

import type { ActiveModal } from '@/components/Nav/DesktopTopbar/components/DesktopTopbarAccountAccess';
import NotificationTag from '@/components/NotificationTag';

import useUser from '@/hooks/useUser';
import { useCompanyLimits } from '@/hooks/useCompanyLimits';
import { useUserAccess } from '@/hooks/useUserAccess';

import { SWS_SUPPORT, SWS_WHATS_NEW_LINK } from '@/constants';
import {
  ROUTE_USER_PROFILE,
  ROUTE_USER_NOTIFICATIONS,
  ROUTE_USER_LOGOUT,
  ROUTE_USER_BETA_LAB,
  ROUTE_PLANS,
  ROUTE_USER_SUBSCRIPTION,
  ROUTE_USER_SUBSCRIPTION_AND_BILLING,
  ROUTE_PRO_API,
  ROUTE_GIFT_CARD_PURCHASE,
  URL_SALES_SITE,
} from '@/constants/routes';

import {
  NavItem,
  NavList,
  NavWrapper,
  NavItemHREF,
  Notification,
  AccountNavigation,
  ViewCompaniesLink,
} from './styled';
import { useFlag } from '@simplywallst/unleash';
import { RenderUntilExpiry } from '@/components/RenderUntilExpiry';
import { useProApiAccess } from '@/pages/ProApi/hooks/useProApiAccess';
import { useTranslation, Translation } from '@/hooks/useTranslation';
import { Badge, Box, Text, Button } from '@simplywallst/ui-core';
import { useSubscriptionRecovery } from '@/hooks/useSubscriptionRecovery';
import { useStripePortal } from '@/hooks/useStripePortal';

interface Props {
  maxViewCompanyReports: number;
  location: string;
  onActivateModal: (activeModal: ActiveModal) => void;
}
export const DesktopTopbarAccountDropdown = ({
  maxViewCompanyReports,
  location,
  onActivateModal,
}: Props) => {
  const limits = useCompanyLimits();
  const { data: userAccess } = useUserAccess();
  const { data: userData } = useUser();
  const { trackEvent } = useTracking({ subject: 'primaryNavDesktopDropdown' });
  const t = useTranslation();

  const proApiFlag = useFlag('pro-api-beta-labs');

  const isTrialUser = userData.billing.status === 'trialing';

  // Avanza users shouldn't be able to change their plan or manage subscriptions, as this is controlled by Avanza.
  const isAvanzaUser = userAccess.brokerReferrer === 'avanza';

  const shouldShowTrialNotification =
    !isTrialUser && maxViewCompanyReports !== -1;

  const shouldShowViewedCompaniesBtn = isTrialUser;

  const { canCreateApiKeys } = useProApiAccess();

  const { isPastDue, planName, planInterval } = useSubscriptionRecovery();
  const { data: stripePortal } = useStripePortal();

  return (
    <>
      <AccountNavigation>
        <NavWrapper>
          {isPastDue && (
            <Notification>
              <Box marginBottom="x2spacePx">
                <Text typography="xSmall">
                  <Translation
                    i18nKey="app.topbar.nav.lapse.copy1"
                    values={{
                      name: planName,
                      interval: planInterval,
                    }}
                  >
                    Your renewal payment for the
                    <Text as="span" typography="strong">
                      . . subscription
                    </Text>
                    recently failed. Your account is temporarily downgraded to
                    the free plan.
                  </Translation>
                </Text>
              </Box>
              <Box marginBottom="x2spacePx">
                <Text typography="xSmall">
                  {t('app.topbar.nav.lapse.copy2', {
                    name: planName,
                    interval: planInterval,
                  })}
                </Text>
              </Box>
              <Box>
                <Text typography="xSmall">
                  <Button
                    styleType="strongLink"
                    onClick={() => {
                      trackEvent({
                        action: 'click',
                        modifier: 'updatePaymentDetails',
                      });
                      window.open(stripePortal?.url, '_blank');
                    }}
                  >
                    {t('app.topbar.nav.lapse.cta')}
                  </Button>
                </Text>
              </Box>
            </Notification>
          )}

          {shouldShowTrialNotification && !isPastDue && (
            <Notification>
              <p>
                <Translation
                  i18nKey="app.topbar.nav.account.viewLimit"
                  values={{
                    views: limits.views,
                    limit: limits.limit,
                    plan: startCase(userAccess.planType),
                  }}
                >
                  <span>You&apos;ve used </span>
                  <NotificationTag alert data-cy-id="company-view-limit-count">
                    {`${limits.views}/${limits.limit}`}
                  </NotificationTag>
                  <span>
                    {` spots of your ${startCase(
                      userAccess.planType
                    )} Plan’s company view limit`}
                  </span>
                </Translation>
              </p>
              <p>
                <ViewCompaniesLink
                  data-cy-id="account-nav-viewed-companies"
                  onClick={() => {
                    onActivateModal('viewedCompanies');
                  }}
                >
                  {t('app.topbar.nav.account.viewCompanies')}
                </ViewCompaniesLink>
              </p>
            </Notification>
          )}
          <NavList hasLimit={maxViewCompanyReports !== -1}>
            <li>
              <NavItem
                to={{ type: ROUTE_USER_PROFILE }}
                inheritColor
                noUnderline
                data-cy-id="account-nav-profile"
                onClick={() =>
                  location !== ROUTE_USER_PROFILE &&
                  trackEvent({ modifier: 'Profile' })
                }
              >
                {t('app.topbar.nav.account.profile')}
              </NavItem>
            </li>
            {RUNTIME_ENV !== 'native' &&
              !isAvanzaUser &&
              !isTrialUser &&
              userAccess.provider === 'stripe' && (
                <li>
                  <NavItem
                    to={{ type: ROUTE_USER_SUBSCRIPTION_AND_BILLING }}
                    inheritColor
                    noUnderline
                    data-cy-id="account-nav-subscription-and-billing"
                    onClick={() => {
                      if (location !== ROUTE_USER_SUBSCRIPTION_AND_BILLING) {
                        trackEvent({ modifier: 'SubscriptionAndBilling' });
                      }
                    }}
                  >
                    {t('app.topbar.nav.account.subscription')}
                  </NavItem>
                </li>
              )}
            {RUNTIME_ENV !== 'native' &&
              !isAvanzaUser &&
              userAccess.provider !== 'stripe' && (
                <li>
                  <NavItem
                    to={{ type: ROUTE_USER_SUBSCRIPTION }}
                    inheritColor
                    noUnderline
                    data-cy-id="account-nav-subscription"
                    onClick={() => {
                      location !== ROUTE_USER_SUBSCRIPTION &&
                        trackEvent({ modifier: 'Subscription' });
                    }}
                  >
                    {t('app.topbar.nav.account.manageSubscription')}
                  </NavItem>
                </li>
              )}
            {RUNTIME_ENV !== 'native' &&
              !isAvanzaUser &&
              userAccess.provider === 'stripe' && (
                <li>
                  <NavItem
                    to={{ type: ROUTE_PLANS }}
                    inheritColor
                    noUnderline
                    data-cy-id="account-nav-plans"
                    onClick={() => trackEvent({ modifier: 'Plans' })}
                  >
                    {t('app.topbar.nav.account.plans')}
                  </NavItem>
                </li>
              )}

            <li>
              <NavItemHREF
                as="a"
                href={URL_SALES_SITE}
                target="_blank"
                rel="noopener"
                data-cy-id="account-nav-products-features"
                onClick={() => trackEvent({ modifier: 'Products & Features' })}
              >
                {t('app.topbar.nav.account.productsFeatures')}
              </NavItemHREF>
            </li>

            {RUNTIME_ENV !== 'native' && (
              <li>
                <NavItem
                  to={{ type: ROUTE_GIFT_CARD_PURCHASE }}
                  inheritColor
                  noUnderline
                  data-cy-id="account-nav-gift-subscription"
                  onClick={() => trackEvent({ modifier: 'GiftSubscription' })}
                >
                  {t('app.topbar.nav.account.giftSubscription')}
                </NavItem>
              </li>
            )}

            {shouldShowViewedCompaniesBtn && (
              <li>
                <NavItem
                  inheritColor
                  noUnderline
                  data-cy-id="account-nav-viewed-companies-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    onActivateModal('viewedCompanies');
                  }}
                >
                  {t('app.topbar.nav.account.viewedCompanies')}
                </NavItem>
              </li>
            )}

            <li>
              <NavItem
                to={{ type: ROUTE_USER_NOTIFICATIONS }}
                inheritColor
                noUnderline
                data-cy-id="account-nav-notifications"
                onClick={() =>
                  location !== ROUTE_USER_NOTIFICATIONS &&
                  trackEvent({ modifier: 'Notifications' })
                }
              >
                {t('app.topbar.nav.account.notifications')}
              </NavItem>
            </li>
            {RUNTIME_ENV !== 'native' && (
              <li>
                <NavItem
                  to={{ type: ROUTE_USER_BETA_LAB }}
                  inheritColor
                  noUnderline
                  data-cy-id="account-nav-beta-lab"
                  onClick={() =>
                    location !== ROUTE_USER_BETA_LAB &&
                    trackEvent({ modifier: 'Beta Lab' })
                  }
                >
                  Beta Lab{' '}
                  {proApiFlag.enabled && (
                    <RenderUntilExpiry
                      startedFrom="2023-06-20"
                      expiresIn={{ months: 6 }}
                    >
                      <Badge tone="attention">
                        {t('app.topbar.nav.account.new')}
                      </Badge>
                    </RenderUntilExpiry>
                  )}
                </NavItem>
              </li>
            )}
            {canCreateApiKeys && (
              <li>
                <NavItem
                  to={{ type: ROUTE_PRO_API }}
                  inheritColor
                  noUnderline
                  data-cy-id="account-nav-pro-dashboard"
                  onClick={() =>
                    location !== ROUTE_PRO_API &&
                    trackEvent({ modifier: 'Pro API' })
                  }
                >
                  {t('app.topbar.nav.account.proAPI')}
                </NavItem>
              </li>
            )}
            <li>
              <NavItemHREF
                as="a"
                styleType="softLink"
                target="_blank"
                rel="noopener noreferrer"
                href={SWS_SUPPORT}
                data-cy-id="account-nav-help"
                onClick={() => trackEvent({ modifier: 'Help Center' })}
              >
                {t('app.topbar.nav.account.help')}
              </NavItemHREF>
            </li>
            <li>
              <NavItemHREF
                as="a"
                styleType="softLink"
                target="_blank"
                rel="noopener noreferrer"
                href={SWS_WHATS_NEW_LINK}
                data-cy-id="account-nav-whats-new"
                onClick={() => trackEvent({ modifier: `What's New` })}
              >
                {t('app.topbar.nav.account.whatsNew')}
              </NavItemHREF>
            </li>
            <li>
              <NavItem
                to={{ type: ROUTE_USER_LOGOUT }}
                inheritColor
                noUnderline
                data-cy-id="account-nav-logout"
                onClick={() => trackEvent({ modifier: 'Log out' })}
              >
                {t('app.topbar.nav.account.logout')}
              </NavItem>
            </li>
          </NavList>
        </NavWrapper>
      </AccountNavigation>
    </>
  );
};
